import { isDefined } from "@clipboard-health/util-ts";
import { CssBaseline, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { RootPaths } from "../App/paths";
import { useLegacyStateContext } from "../LegacyStateBridge/context";
import { ClipboardLogo } from "../lib";
import { PageFooterWrapper } from "../redesign/components/PageFooterWrapper";
import { PageWrapper } from "../redesign/components/PageWrapper";

export function ReactivateSuccessPage() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const {
    session: { authStateHasLoaded, userId },
  } = useLegacyStateContext();

  // Reactivation just set `custom:cbh_user_id` (and email) on the caller's
  // Cognito user server-side. Force a token refresh so the next request the
  // app makes carries an ID token that reflects the new attributes —
  // otherwise the app keeps presenting as an OnboardingUser and the BFF
  // keeps returning the SOFT_DELETED_ACCOUNT_FOUND 409.
  useEffect(() => {
    void (async () => {
      try {
        await getCognitoSingleton().getIdToken(true);
        await invalidateGetWorkerQuery(queryClient);
      } catch (error) {
        logError(APP_V2_APP_EVENTS.FAILED_TO_REFRESH_COGNITO_TOKEN, { error });
      }
    })();
  }, [queryClient]);

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <PageWrapper variant="tertiary">
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ flex: 1, paddingBottom: 20 }}
          gap={4}
        >
          <ClipboardLogo width={100} />
          <Title component="h4">Your account has been reactivated!</Title>
          <Text>You can now log in to your account.</Text>
        </Stack>

        <PageFooterWrapper>
          <LoadingButton
            fullWidth
            variant="contained"
            isLoading={!authStateHasLoaded || !isDefined(userId)}
            onClick={() => {
              history.push(RootPaths.HOME);
            }}
          >
            Continue
          </LoadingButton>
        </PageFooterWrapper>
      </PageWrapper>
    </ThemeProvider>
  );
}
